/**
 * Empire Pro — Boost card enhancer
 *
 * Populates Money-typed metafields, the sale badge, and the conditional
 * promo row on Boost-rendered product cards. The corresponding template
 * markup lives in snippets/boost-pfs-filter-html.liquid (productGridItemHtml).
 *
 * Why this file exists: Boost's runtime substitutes {{product.metafields.<ns>.<key>}}
 * placeholders directly into the template, but Money metafields come back as
 * JSON-encoded strings ({"amount":"X.XX","currency_code":"USD"}). The embedded
 * double quotes break HTML attributes, and the literal JSON would render in
 * spans. So the template emits empty placeholders and this script populates
 * them after each render.
 *
 * Three data sources:
 *   1. JSONP responses: BoostPFSFilterCallback fires on every filter/sort/
 *      pagination/auto-sort. We wrap it to cache product data by ID before
 *      Boost's render runs. Used for non-price fields (SKU, in_promo_catalog
 *      badge gate, MSRP/MAP/Dealer Money metafields).
 *   2. The grid DOM: a MutationObserver watches for re-renders and walks new
 *      cards, populating each from the JSONP cache.
 *   3. /products/<handle>.js (Phase 8.5): Boost's JSONP API carries no Shopify
 *      customer session, so product.price_min is the published price — never
 *      the customer's B2B catalog price. We can't gate the Promo row on JSONP
 *      data because dealer customers on a B2B catalog see the wrong comparison.
 *      Fix: per visible card with a dealer Promo row, fetch /products/<handle>.js
 *      (which DOES carry the session and returns the customer's catalog price),
 *      compare against the dealer-price metafield, show/hide accordingly.
 *      See boost-trial-plan.md "Phase 8.5".
 */

(function () {
  if (typeof window === 'undefined') return;

  // Cache of products keyed by id (string). Populated from every JSONP
  // response Boost receives — initial auto-sort plus filter/sort/page calls.
  const productCache = new Map();

  function cacheProducts(data) {
    if (!data || !Array.isArray(data.products)) return;
    for (const p of data.products) {
      if (p && p.id != null) productCache.set(String(p.id), p);
    }
  }

  // Wrap the JSONP callback. Try once immediately, then poll briefly in case
  // our script loads before Boost defines it. Once wrapped, our pre-hook
  // captures the response and we delegate to the original.
  function wrapCallback() {
    const orig = window.BoostPFSFilterCallback;
    if (typeof orig !== 'function' || orig.__empire_wrapped) return false;
    const wrapped = function (data) {
      cacheProducts(data);
      return orig.apply(this, arguments);
    };
    wrapped.__empire_wrapped = true;
    window.BoostPFSFilterCallback = wrapped;
    return true;
  }

  if (!wrapCallback()) {
    let attempts = 0;
    const interval = setInterval(() => {
      if (wrapCallback() || ++attempts > 50) clearInterval(interval);
    }, 100);
  }

  // Currency formatters cached by code so we don't rebuild Intl objects.
  const moneyFormatters = new Map();

  function formatMoney(jsonValue) {
    if (!jsonValue) return '';
    let parsed;
    try {
      parsed = JSON.parse(jsonValue);
    } catch (e) {
      return '';
    }
    const amount = parseFloat(parsed.amount);
    if (!isFinite(amount)) return '';
    const code = parsed.currency_code || 'USD';
    let fmt = moneyFormatters.get(code);
    if (!fmt) {
      fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: code });
      moneyFormatters.set(code, fmt);
    }
    return fmt.format(amount);
  }

  function getMetafield(product, key) {
    if (!product || !Array.isArray(product.metafields)) return null;
    const m = product.metafields.find(
      (mf) => mf && mf.namespace === 'custom' && mf.key === key
    );
    return m ? m.value : null;
  }

  // Mirrors snippets/promo-active.liquid. Active when in_promo_catalog is set
  // AND (no end date OR end date is today or later). Date metafields come back
  // from Boost JSONP as ISO strings ("YYYY-MM-DD" or full ISO datetime); slice
  // first 10 chars and compare lexicographically to today's ISO date.
  function isPromoActive(product) {
    const inPromo = getMetafield(product, 'in_promo_catalog');
    if (inPromo !== 'true' && inPromo !== true) return false;
    const endRaw = getMetafield(product, 'promo_end_date');
    if (!endRaw) return true;
    const endDate = String(endRaw).slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return endDate >= today;
  }

  function setRow(card, rowName, formattedText) {
    const row = card.querySelector('.price-table__row[data-row="' + rowName + '"]');
    if (!row) return;
    if (formattedText) {
      const valueEl = row.querySelector('.price-table__value');
      if (valueEl) valueEl.textContent = formattedText;
      row.removeAttribute('hidden');
    } else {
      row.setAttribute('hidden', '');
    }
  }

  function enhanceCard(card) {
    const productId = card.getAttribute('data-product-id');
    if (!productId) return;
    const product = productCache.get(productId);
    if (!product) return;

    setRow(card, 'msrp', formatMoney(getMetafield(product, 'msrp_price')));
    setRow(card, 'map',  formatMoney(getMetafield(product, 'map_price')));

    const dealerRaw = getMetafield(product, 'dealer_price');
    setRow(card, 'dealer', formatMoney(dealerRaw));

    // Promo row is handled separately in enhancePromoRow — it needs an
    // async fetch to /products/<handle>.js for B2B-aware sale_price.

    // Sale badge — gated on custom.in_promo_catalog AND custom.promo_end_date
    // (defensive: Acumatica's Stock Item export can lag clearing in_promo_catalog
    // past the end date). Mirrors snippets/promo-active.liquid for consistency.
    // Note: requires custom.promo_end_date to be registered in Boost Settings →
    // Metafields. If unregistered, the date is undefined and we fall back to
    // in_promo_catalog only (graceful degradation; matches prior behavior).
    const badge = card.querySelector('.empire-sale-badge');
    if (badge) {
      if (isPromoActive(product)) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    }

    // SKU — pulled from the first variant in Boost's product payload.
    // The server-side template renders SKU directly via Liquid, but client
    // re-renders need this to repopulate. data-sku="" is the empty/hidden
    // state matched by the [data-sku=""] CSS rule.
    const skuEl = card.querySelector('.product-sku');
    if (skuEl) {
      const variantSku = product.variants && product.variants[0] && product.variants[0].sku;
      if (variantSku) {
        skuEl.textContent = 'SKU: ' + variantSku;
        skuEl.setAttribute('data-sku', variantSku);
      } else {
        skuEl.textContent = '';
        skuEl.setAttribute('data-sku', '');
      }
    }
  }

  // ----- Phase 8.5+: /products/<handle>.js cache ------------------------
  // Cache of in-flight and resolved product fetches, keyed by handle. Storing
  // the Promise (not the resolved value) means concurrent requests for the
  // same handle attach to the same network call instead of firing duplicates —
  // important under infinite scroll, where rapid mutations trigger overlapping
  // enhanceGrid scans before the first batch resolves. Lives for the session;
  // B2B catalog prices don't change mid-session.
  //
  // Two consumers:
  //   - Promo row (Phase 8.5): needs data.price for the B2B-aware comparison.
  //   - Inline ATC (Phase 9): needs data.variants[0].id and .available.
  // Both resolve from the same payload; one fetch covers both.
  const productByHandle = new Map();

  function fetchProductByHandle(handle) {
    const cached = productByHandle.get(handle);
    if (cached) return cached;
    const promise = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then((resp) => {
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      return resp.json();
    });
    // Evict on rejection so a transient network failure doesn't poison the
    // cache for the rest of the session. The next consumer call retries.
    promise.catch(() => {
      if (productByHandle.get(handle) === promise) {
        productByHandle.delete(handle);
      }
    });
    productByHandle.set(handle, promise);
    return promise;
  }

  // Render epoch — incremented on every enhanceGrid call. A fetch resolved
  // against an older epoch belongs to a render Boost has since replaced;
  // its DOM update is dropped to avoid overwriting the current render with
  // stale data. Fetches still resolve and populate priceCacheByHandle so the
  // newer render reuses the work.
  let renderEpoch = 0;

  async function enhancePromoRow(card, epoch) {
    // Server-side template only emits data-row="promo" for dealer customers
    // (customer.tags contains 'approved'). DOM presence is the gate — no
    // fetch fires for anonymous or non-dealer traffic.
    const promoRow = card.querySelector('.price-table__row[data-row="promo"]');
    if (!promoRow) return;

    const productId = card.getAttribute('data-product-id');
    const product = productCache.get(productId);
    if (!product || !product.handle) return;

    // Defensive metafield gate — mirrors blocks/price.liquid. The price
    // comparison (sale < dealer) alone fires whenever Acumatica's B2B price
    // list still has the discount, even past the promo end date.
    if (!isPromoActive(product)) {
      promoRow.setAttribute('hidden', '');
      return;
    }

    const dealerRaw = getMetafield(product, 'dealer_price');
    let dealerAmount = null;
    if (dealerRaw) {
      try { dealerAmount = parseFloat(JSON.parse(dealerRaw).amount); } catch (e) { /* keep null */ }
    }
    if (dealerAmount == null || !isFinite(dealerAmount)) {
      promoRow.setAttribute('hidden', '');
      return;
    }

    let productData;
    try {
      productData = await fetchProductByHandle(product.handle);
    } catch (e) {
      promoRow.setAttribute('hidden', '');
      return;
    }

    if (epoch !== renderEpoch) return;

    // data.price is the lowest variant price in cents. /products/<handle>.js
    // carries the Shopify customer session, so for logged-in B2B customers
    // this reflects the catalog price, not the published price.
    const salePriceDollars = productData.price / 100;

    if (isFinite(salePriceDollars) && salePriceDollars < dealerAmount) {
      const valueEl = promoRow.querySelector('.promo-value');
      if (valueEl) {
        valueEl.textContent = formatMoney(
          JSON.stringify({ amount: salePriceDollars.toFixed(2), currency_code: 'USD' })
        );
      }
      promoRow.removeAttribute('hidden');
    } else {
      promoRow.setAttribute('hidden', '');
    }
  }

  // ----- Phase 9: Inline ATC -------------------------------------------
  // The client-side card template (boost-pfs-filter-html.liquid) renders the
  // ATC form with an empty variantId input and the submit buttons disabled,
  // because Boost's runtime can't substitute a Liquid product.selected_or_
  // first_available_variant.id into the template. Fill them in here.
  //
  // DOM contract:
  //   <form data-empire-boost-atc-form>
  //     <input ref="variantId" name="id" value="" disabled>
  //     <button class="card-qty-stepper__atc-default" disabled>...</button>
  //     <button data-action="submit" disabled>...</button>  (qty stepper)
  //
  // Server-rendered cards (boost-pfs-filter-product-item.liquid) already have
  // the variant id populated via Liquid; this skips them via the [disabled]
  // selector on the variantId input. Initial page load uses the server path,
  // so cards aren't enabled twice.
  async function enhanceAddToCart(card, epoch) {
    const form = card.querySelector('form[data-empire-boost-atc-form]');
    if (!form) return;
    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.disabled) return;

    const productId = card.getAttribute('data-product-id');
    const product = productCache.get(productId);
    if (!product || !product.handle) return;

    let productData;
    try {
      productData = await fetchProductByHandle(product.handle);
    } catch (e) {
      return;
    }

    if (epoch !== renderEpoch) return;

    const firstVariant = productData.variants && productData.variants[0];
    if (!firstVariant) return;

    variantInput.value = firstVariant.id;
    variantInput.disabled = false;

    if (firstVariant.available) {
      const atcBtn = form.querySelector('.card-qty-stepper__atc-default');
      if (atcBtn) atcBtn.disabled = false;
      const submitBtn = form.querySelector('[data-action="submit"]');
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  function enhanceGrid(grid) {
    renderEpoch++;
    const epoch = renderEpoch;
    const cards = grid.querySelectorAll('.boost-pfs-filter-product-item[data-product-id]');
    for (const card of cards) {
      enhanceCard(card);
      enhancePromoRow(card, epoch);
      enhanceAddToCart(card, epoch);
    }
  }

  function watchGrid(grid) {
    if (grid.__empire_watched) return;
    grid.__empire_watched = true;
    const observer = new MutationObserver(() => enhanceGrid(grid));
    observer.observe(grid, { childList: true });
    enhanceGrid(grid);
  }

  // ----- Sort button label sync ---------------------------------------
  // Boost's button text is hardcoded to the static "Sort By" label rather
  // than the active sort. Mirror the active <a class="boost-pfs-filter-
  // sort-item-active"> text into the button so it reflects the current
  // sort (e.g. "Relevance"). The dropdown is re-rendered by Boost on every
  // sort change, so a MutationObserver on the sort container keeps the
  // button label in sync without depending on Boost's internal events.
  function updateSortButtonLabel() {
    const button = document.querySelector('.boost-pfs-filter-top-sorting-wrapper');
    if (!button) return;
    const container = button.closest('.boost-pfs-filter-top-sorting');
    if (!container) return;
    const dropdown = container.querySelector('.boost-pfs-filter-filter-dropdown');
    if (!dropdown) return;
    const activeLink =
      dropdown.querySelector('a.boost-pfs-filter-sort-item-active') ||
      dropdown.querySelector('a');
    if (!activeLink) return;
    const text = activeLink.textContent.trim();
    if (!text) return;
    const innerSpan = button.querySelector('span > span');
    if (innerSpan && innerSpan.textContent.trim() !== text) {
      innerSpan.textContent = text;
    }
    button.setAttribute('aria-label', 'Sort by: ' + text);
  }

  function watchSortToolbar() {
    const sortContainer = document.querySelector('.boost-pfs-filter-custom-sorting');
    if (!sortContainer || sortContainer.__empire_sort_watched) return true;
    sortContainer.__empire_sort_watched = true;
    const observer = new MutationObserver(updateSortButtonLabel);
    observer.observe(sortContainer, { childList: true, subtree: true });
    updateSortButtonLabel();
    return true;
  }

  function init() {
    document.querySelectorAll('.boost-pfs-filter-products').forEach(watchGrid);

    // Sort toolbar may not exist yet at DOMContentLoaded — Boost injects
    // it after its filter init. Poll briefly until it's there, mirroring
    // the wrapCallback retry pattern above.
    if (!document.querySelector('.boost-pfs-filter-custom-sorting .boost-pfs-filter-top-sorting-wrapper')) {
      let attempts = 0;
      const interval = setInterval(() => {
        const ready = document.querySelector('.boost-pfs-filter-custom-sorting .boost-pfs-filter-top-sorting-wrapper');
        if ((ready && watchSortToolbar()) || ++attempts > 50) clearInterval(interval);
      }, 100);
    } else {
      watchSortToolbar();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
